<template>
  <v-dialog v-model="dialog" persistent max-width="1000px">
    <template v-slot:activator="{ on }">
      <v-btn v-if="isEditButton" icon v-on="on">
        <v-icon>fa-edit</v-icon>
      </v-btn>
      <v-btn v-else color="info" dark v-on="on">
        <v-icon class="pr-1">fa-plus-circle</v-icon>
          Add New
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="text-h5">Account Type Mapping Rule</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row >
            <v-col cols="12">
              <v-autocomplete label="Kasasa Account Type"
                :items="kasasaAccountTypes"
                item-text="text"
                item-value="key"
                v-model="workingRule.kasasaAccountType"
                @change="onModification"
                :loading="loading"/>
            </v-col>

            <v-col cols="12">
              <v-combobox label = "Core Account Types"
                id="coreTypes"
                multiple
                return-object
                v-model="workingRule.coreAccountTypes"
                append-icon
                chips
                deletable-chips
                :rules="requiredRule"
                :search-input.sync="enteredCoreType"
                @update:search-input="splitIntoCoreTypes"
                @change="onModification">
              </v-combobox>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn id="cancelButton" large @click="cancel" :disabled="saving">Cancel</v-btn>
          <v-btn id="saveButton" large class="blue white--text" @click="save"
            :loading="saving" :disabled="!isModificationSavable">
              Save
          </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import {
  Component, Prop, Watch, Inject,
} from 'vue-property-decorator';
import { AccountTypeMappingRuleResource, AccountTypeMappingRule }
  from '@/api/accounttypes/';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';
import { StringTool } from '@/util';

@Component({ computed: mapState(['fiId']) })
export default class AccountTypeMappingRuleDialog extends Vue {
  @Inject('AccountTypeMappingRuleResource')
  ruleResource!:AccountTypeMappingRuleResource;

  @Prop()
  rule!:AccountTypeMappingRule;

  @Prop({ default: [] })
  existingRules!:Array<AccountTypeMappingRule>;

  @Prop()
  isEditButton!:boolean;

  @Prop()
  accountTypeMap!:Map<string, string>;

  workingRule:AccountTypeMappingRule =
    AccountTypeMappingRuleDialog.createEmptyRule();

  fiId!:string;

  dialog:boolean = false;

  saving:boolean = false;

  enteredCoreType:string = '';

  kasasaAccountTypes:{ [key: string]: string }[] = [];

  isModificationSavable:boolean = false;

  loading = false;

  delimiter: string = ',';

  requiredRule:Array<any> = [(value: any) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'Required';
    }
    return true;
  }];

  static createEmptyRule() : AccountTypeMappingRule {
    return {
      id: undefined,
      coreAccountTypes: [],
      kasasaAccountType: '',
    };
  }

  @Watch('dialog')
  onDialogToggle() {
    if (this.dialog) {
      this.saving = false;
      this.loading = true;
      this.initializeWorkingRule();
      if (this.kasasaAccountTypes.length === 0
        && this.accountTypeMap !== undefined) {
        this.buildKasasaAccountTypesFromMap();
      }
      this.loading = false;
    }
  }

  initializeWorkingRule() {
    this.isModificationSavable = false;
    if (this.rule) {
      Object.assign(this.workingRule, this.rule);
    } else {
      this.workingRule = AccountTypeMappingRuleDialog.createEmptyRule();
    }
  }

  buildKasasaAccountTypesFromMap() {
    this.accountTypeMap.forEach((value: string, key: string) => {
      const text:string = value;
      this.kasasaAccountTypes.push({
        key,
        text,
      });
    });
  }

  cancel() {
    this.initializeWorkingRule();
    this.dialog = false;
  }

  save() {
    // Force combos to update because they might still have focus when user clicks save.
    if (this.enteredCoreType && this.enteredCoreType.trim()
      && !this.workingRule.coreAccountTypes.includes(this.enteredCoreType.trim())) {
      this.workingRule.coreAccountTypes.push(this.enteredCoreType.trim());
    }

    this.saving = true;
    if (!this.areWorkingCoresUnique()) {
      this.saving = false;
      this.setError('Duplicate Core Account Type!');
      return;
    }

    const warnings = this.trimTabsFromWorkingRuleAndGetWarnings();
    if (warnings.length !== 0) {
      this.showWarnings('Warning, Leading/Trailing tab characters were trimmed from', warnings);
    }

    this.ruleResource.save(this.fiId, this.workingRule)
      .then((savedRule:AccountTypeMappingRule) => {
        if (this.rule) {
          Object.assign(this.rule, savedRule);
        }
        this.$emit('success', savedRule);
        this.dialog = false;
        this.initializeWorkingRule();
      })
      .catch((error:any) => {
        this.saving = false;
        this.showError();
      });
  }

  areWorkingCoresUnique(): boolean {
    const allCores = new Set<string>();
    this.existingRules.forEach((rule) => {
      if (rule.id !== this.workingRule.id) {
        rule.coreAccountTypes.forEach((coreType) => {
          allCores.add(coreType);
        });
      }
    });
    let isUnique = true;
    this.workingRule.coreAccountTypes.forEach((coreType) => {
      if (allCores.has(coreType)) {
        isUnique = false;
      }
    });
    return isUnique;
  }

  splitIntoCoreTypes() {
    if (this.enteredCoreType && this.enteredCoreType.split(this.delimiter).length > 1) {
      this.workingRule.coreAccountTypes.push(...this.enteredCoreType
        .split(this.delimiter).filter(term => !this.workingRule.coreAccountTypes.includes(term) && term.trim() !== ''));
      this.enteredCoreType = '';
      this.onModification();
    }
  }

  onModification() {
    const oldCores = new Set<string>();
    if (this.rule) {
      this.rule.coreAccountTypes.forEach((coreType) => {
        oldCores.add(coreType);
      });
    }
    const newCores = new Set<string>();
    if (this.workingRule && this.workingRule.coreAccountTypes) {
      this.workingRule.coreAccountTypes.forEach((coreType) => {
        newCores.add(coreType);
      });
    }
    const deletedCores = new Set([...oldCores].filter(x => !newCores.has(x)));
    const addedCores = new Set([...newCores].filter(x => !oldCores.has(x)));

    this.isModificationSavable = this.workingRule.kasasaAccountType !== '' // kasasa type selected
      && this.workingRule.coreAccountTypes.length > 0 // non empty core type
      && (deletedCores.size > 0 // core type removed
          || addedCores.size > 0 // core type added
          || (this.rule
              && this.rule.kasasaAccountType // kasasa type modified
              !== this.workingRule.kasasaAccountType));
  }

  trimTabsFromWorkingRuleAndGetWarnings() : Array<string> {
    const warnings: Array<string> = [];
    if (!this.workingRule) {
      return warnings;
    }

    let valueBefore = this.workingRule.kasasaAccountType;
    let valueAfter = StringTool.trimTabs(valueBefore);
    if (valueBefore !== valueAfter) {
      warnings.push(`"${valueBefore}"`);
      this.workingRule.kasasaAccountType = valueAfter!;
    }

    if (!this.workingRule.coreAccountTypes) {
      return warnings;
    }

    for (let i = 0; i < this.workingRule.coreAccountTypes.length; i++) {
      valueBefore = this.workingRule.coreAccountTypes[i];
      valueAfter = StringTool.trimTabs(valueBefore);
      if (valueBefore !== valueAfter) {
        warnings.push(`"${valueBefore}"`);
        this.workingRule.coreAccountTypes[i] = valueAfter!;
      }
    }

    return warnings;
  }

  showWarnings(warningLabel: string, warnings: Array<string>) {
    const joinedWarnings = warnings.join(', ');
    const warningsString: string = `${warningLabel}: ${joinedWarnings}`;
    this.showWarning(warningsString);
  }

  showError() {
    const al = new Alert('An unexpected error occurred.', NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  showWarning(warning: string) {
    const al = new Alert(warning, NoticeClass.WARN);
    al.setTimeout(20000);
    this.$store.dispatch('notices/add', al);
  }

  setError(message:string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss" scoped>
</style>
